import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const CLAIMED_ATTRIBUTE = 'data-c-offer-modal-claimed';

const SESSION_KEYS = {
  second_item: 'c-offer-modal-shown',
  tshirt: 'c-offer-modal-tshirt-shown',
  fallback: 'c-offer-modal-fallback-shown',
};

const INTRO_USED_KEY = 'c-offer-intro-used';

const ELIGIBLE_IN_CART_LIMIT = 2;

class COfferModal extends HTMLElement {
  /** @type {Record<string, {size: string, price: number}>} */
  #variants = {};

  #eligibleInCart = 0;

  /** @type {string[]} */
  #eligibleProductIds = [];

  #offerKind = 'second_item';

  #triggerProductId = '';

  connectedCallback() {
    this.#eligibleInCart = Number(this.dataset.eligibleInCart) || 0;
    this.#eligibleProductIds = (this.dataset.eligibleProductIds ?? '').split(',').filter(Boolean);
    this.#offerKind = SESSION_KEYS[this.dataset.offerKind ?? ''] ? this.dataset.offerKind : 'second_item';
    this.#triggerProductId = String(this.dataset.triggerProductId ?? '');

    const variantData = this.querySelector('[data-c-offer-variants]');
    if (variantData?.textContent) {
      try {
        this.#variants = JSON.parse(variantData.textContent);
      } catch {
        this.#variants = {};
      }
    }

    // Capture phase, not bubble: the cart drawer listens for the same event on
    // the same target, and only running first lets this claim the event before
    // the drawer auto-opens on it.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { capture: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#refreshModule);
    document.addEventListener('click', this.#handleModuleAdd);

    for (const button of this.querySelectorAll('[data-c-offer-close]')) {
      button.addEventListener('click', this.close);
    }
    this.querySelector('[data-c-offer-add]')?.addEventListener('click', this.#handleAdd);
    this.querySelector('[data-c-offer-more]')?.addEventListener('click', this.#revealMore);
    this.addEventListener('click', this.#handleSizeClick);
    this.addEventListener('keydown', this.#handleSizeKeydown);
    this.addEventListener('change', this.#handleOptionChange);

    for (const option of this.#options) this.#selectDefaultSize(option);
    const currentSizes = this.querySelector('[data-c-offer-current-sizes]');
    if (currentSizes) this.#selectDefaultSize(currentSizes);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { capture: true });
    document.removeEventListener(ThemeEvents.cartUpdate, this.#refreshModule);
    document.removeEventListener('click', this.#handleModuleAdd);
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-c-offer-dialog]'));
  }

  get #options() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-c-offer-option]')]);
  }

  get #checkedOption() {
    return this.#options.find(
      (option) => /** @type {HTMLInputElement | null} */ (option.querySelector('[data-c-offer-radio]'))?.checked
    );
  }

  #handleCartUpdate = (event) => {
    const detail = event.detail?.data;
    if (!detail || detail.didError) return;
    if (detail.source !== 'product-form-component') return;

    const addedProductId = String(detail.productId ?? '');

    if (this.#offerKind === 'fallback') {
      if (addedProductId !== this.#triggerProductId) return;
    } else {
      if (!this.#eligibleProductIds.includes(addedProductId)) return;
      this.#eligibleInCart += Number(detail.itemCount) || 1;
    }

    if (!this.#canFire()) return;

    document.documentElement.setAttribute(CLAIMED_ATTRIBUTE, '');
    requestAnimationFrame(() => document.documentElement.removeAttribute(CLAIMED_ATTRIBUTE));

    this.open({ addedVariantId: this.#addedVariantId(event) });
  };

  #handleModuleAdd = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-c-offer-module-add]') : null;
    if (!button) return;

    event.preventDefault();
    this.open({ state: 'current', selectHandle: button.dataset.handle });
  };

  /**
   * The offer module is for a customer who has not got the second hoodie yet.
   * Liquid drops it at page render; this keeps it right for a cart that changes
   * without one. Counted from the live cart rather than from a running total,
   * so removing a hoodie brings the module back.
   *
   * @param {CustomEvent} [event]
   */
  #refreshModule = async (event) => {
    const module = /** @type {HTMLElement | null} */ (document.querySelector('[data-c-offer-module]'));
    if (!module) return;

    const resource = event?.detail?.resource;
    const cart =
      resource && Array.isArray(resource.items) && typeof resource.item_count === 'number'
        ? resource
        : await fetch(`${Theme.routes.cart_url}.js`)
            .then((response) => response.json())
            .catch(() => null);
    if (!cart) return;

    const eligibleIds = (module.dataset.eligibleProductIds ?? '').split(',').filter(Boolean);
    let units = 0;
    for (const item of cart.items ?? []) {
      if (eligibleIds.includes(String(item.product_id ?? ''))) units += Number(item.quantity) || 0;
    }

    module.hidden = units >= ELIGIBLE_IN_CART_LIMIT;
  };

  #canFire() {
    if (this.#dialog?.open) return false;
    if (this.#introOfferUsed()) return false;
    if (this.#sessionShown()) return false;
    if (this.#eligibleInCart >= ELIGIBLE_IN_CART_LIMIT) return false;
    return true;
  }

  #introOfferUsed() {
    try {
      return localStorage.getItem(INTRO_USED_KEY) === '1';
    } catch {
      return false;
    }
  }

  #sessionKey() {
    return SESSION_KEYS[this.#offerKind] ?? SESSION_KEYS.second_item;
  }

  #sessionShown() {
    try {
      return sessionStorage.getItem(this.#sessionKey()) === '1';
    } catch {
      // Unreadable storage cannot tell us whether this already fired, so treat
      // it as shown. Firing twice is worse than not firing.
      return true;
    }
  }

  #markSessionShown() {
    try {
      sessionStorage.setItem(this.#sessionKey(), '1');
    } catch {
      /* nothing to do */
    }
  }

  /**
   * product-form-component sends the variant id as `sourceId` for a single add
   * but an element id for a batch add, so the DOM lookup is the batch fallback.
   *
   * @param {CustomEvent} event
   */
  #addedVariantId(event) {
    const sourceId = String(event.detail?.sourceId ?? '');
    if (sourceId && this.#variants[sourceId]) return sourceId;

    const form = document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('[ref="variantId"]'));
    return input?.value ?? '';
  }

  /** @param {string} message */
  #announce(message) {
    const region = this.querySelector('[data-c-offer-status]');
    if (region) region.textContent = message;
  }

  /** @param {{addedVariantId?: string, state?: 'added' | 'current', selectHandle?: string}} [options] */
  open({ addedVariantId = '', state = 'added', selectHandle = '' } = {}) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;

    this.hidden = false;
    this.#renderConfirmation(addedVariantId, state);
    this.preselectSize(this.#variants[addedVariantId]?.size ?? '');
    if (selectHandle) this.#selectOption(selectHandle);

    dialog.showModal();
    this.#markSessionShown();
  }

  /** @param {string} handle */
  #selectOption(handle) {
    const radio = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[data-c-offer-radio][value="${CSS.escape(handle)}"]`)
    );
    if (!radio || radio.checked) return;
    radio.checked = true;
    const option = radio.closest('[data-c-offer-option]');
    if (option) this.#selectDefaultSize(option);
  }

  close = () => {
    this.#dialog?.close();
  };

  /**
   * The size chosen on the current-hoodie card, which only exists in the
   * 'current' state. In the 'added' state that hoodie is already in the cart,
   * so there is nothing here to add.
   */
  #currentVariantId() {
    const wrap = /** @type {HTMLElement | null} */ (this.querySelector('[data-c-offer-confirmation]'));
    if (wrap?.dataset.state !== 'current') return '';
    const size = wrap.querySelector('[data-c-offer-current-sizes] [data-c-offer-size][aria-checked="true"]');
    return size?.getAttribute('data-variant-id') ?? '';
  }

  // The rest of the eligible set is already in the page, rendered hidden by
  // Liquid. Revealing it must stay a DOM operation: the modal has a 300ms
  // budget and fetches nothing to open or to expand.
  #revealMore = () => {
    for (const option of this.querySelectorAll('[data-c-offer-extra]')) {
      option.hidden = false;
    }
    const button = /** @type {HTMLElement | null} */ (this.querySelector('[data-c-offer-more]'));
    if (button) button.hidden = true;
  };

  /**
   * @param {string} addedVariantId
   * @param {'added' | 'current'} state
   */
  #renderConfirmation(addedVariantId, state) {
    const wrap = /** @type {HTMLElement | null} */ (this.querySelector('[data-c-offer-confirmation]'));
    if (!wrap) return;

    wrap.hidden = false;
    wrap.dataset.state = state;
    if (state !== 'added') return;

    const variant = this.#variants[addedVariantId];
    const sizeElement = this.querySelector('[data-c-offer-confirmation-size]');
    if (sizeElement) {
      const prefix = this.dataset.sizePrefix ?? '';
      sizeElement.textContent = variant?.size ? `${prefix}${variant.size}` : '';
    }
  }

  #selectDefaultSize(option) {
    const sizes = [...option.querySelectorAll('[data-c-offer-size]')];
    const alreadyChosen = sizes.find((size) => size.getAttribute('aria-checked') === 'true');
    if (!alreadyChosen) {
      const firstAvailable = sizes.find((size) => size.dataset.unavailable !== 'true');
      if (firstAvailable) firstAvailable.setAttribute('aria-checked', 'true');
    }
    this.#updateRovingTabindex(option);
  }

  /** @param {Element} option */
  #updateRovingTabindex(option) {
    const sizes = [...option.querySelectorAll('[data-c-offer-size]')];
    const selectable = sizes.filter((size) => size.dataset.unavailable !== 'true');
    const checked = selectable.find((size) => size.getAttribute('aria-checked') === 'true');
    const stop = checked ?? selectable[0];

    for (const size of sizes) {
      size.setAttribute('tabindex', size === stop ? '0' : '-1');
    }
  }

  /** @param {string} size */
  preselectSize(size) {
    if (!size) return;
    for (const option of this.#options) {
      // data-size, not text content: a chip's text carries a visually-hidden
      // label after the visible one, so "S" would prefix-match "XS".
      const match = [...option.querySelectorAll('[data-c-offer-size]')].find(
        (button) => button.dataset.size === size && button.dataset.unavailable !== 'true'
      );
      if (!match) continue;
      for (const button of option.querySelectorAll('[data-c-offer-size]')) {
        button.setAttribute('aria-checked', 'false');
      }
      match.setAttribute('aria-checked', 'true');
      this.#updateRovingTabindex(option);
    }
  }

  #handleOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('[data-c-offer-radio]')) return;
    const option = event.target.closest('[data-c-offer-option]');
    if (option) this.#selectDefaultSize(option);
  };

  #handleSizeClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-c-offer-size]') : null;
    if (!button) return;
    if (button.dataset.unavailable === 'true') return;

    const group = button.closest('[data-c-offer-sizes]');
    for (const sibling of group?.querySelectorAll('[data-c-offer-size]') ?? []) {
      sibling.setAttribute('aria-checked', 'false');
    }
    button.setAttribute('aria-checked', 'true');

    this.#updateRovingTabindex(button.closest('[data-c-offer-option]') ?? group);
  };

  #handleSizeKeydown = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-c-offer-size]') : null;
    if (!button) return;

    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    const group = button.closest('[data-c-offer-sizes]');
    const buttons = [...(group?.querySelectorAll('[data-c-offer-size]:not([data-unavailable="true"])') ?? [])];
    if (buttons.length === 0) return;

    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const current = buttons.indexOf(button);
    const from = current === -1 ? 0 : current;
    const next = buttons[(from + step + buttons.length) % buttons.length];

    for (const sibling of group?.querySelectorAll('[data-c-offer-size]') ?? []) {
      sibling.setAttribute('aria-checked', 'false');
    }
    next.setAttribute('aria-checked', 'true');

    this.#updateRovingTabindex(button.closest('[data-c-offer-option]') ?? group);
    next.focus();
  };

  #handleAdd = async () => {
    const option = this.#checkedOption;
    const cta = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-c-offer-add]'));
    if (!option || !cta) return;

    const size = option.querySelector('[data-c-offer-size][aria-checked="true"]');
    const variantId = size?.getAttribute('data-variant-id');
    if (!variantId) return;

    // Entered from the offer module, the hoodie being viewed is not in the cart
    // yet, so the button adds it alongside the second one.
    const items = [];
    const currentVariantId = this.#currentVariantId();
    if (currentVariantId) items.push({ id: Number(currentVariantId), quantity: 1 });
    items.push({ id: Number(variantId), quantity: 1 });

    cta.setAttribute('aria-busy', 'true');
    cta.disabled = true;

    try {
      // Theme.routes.cart_add_url already ends in .js, and carries any market
      // or locale prefix. Appending .js gives /cart/add.js.js, which 404s.
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.description || payload?.message || `Cart add failed: ${response.status}`);
      }

      this.#eligibleInCart += items.length;
      this.close();

      document.dispatchEvent(new CartUpdateEvent(payload, this.id, { source: 'c-offer-modal' }));

      const drawer = document.querySelector('c-cart-rodney-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url;
      }
    } catch (error) {
      console.error(error);
      this.#announce(error instanceof Error ? error.message : String(error));
      cta.removeAttribute('aria-busy');
      cta.disabled = false;
    }
  };
}

if (!customElements.get('c-offer-modal')) customElements.define('c-offer-modal', COfferModal);

export { COfferModal };
